import type {
  StorefrontBuyerDeviceRegistration,
  StorefrontCatalogKeyEnvelope,
} from '@stokmerter/contracts';

type StoredBuyerDevice = StorefrontBuyerDeviceRegistration & {
  encryptionPrivateKey: CryptoKey;
  signingPrivateKey: CryptoKey;
};

const DATABASE_NAME = 'stoksy-storefront-security';
const STORE_NAME = 'buyer-device';
const CURRENT_KEY = 'current';

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value.replaceAll('-', '+').replaceAll('_', '/');
  const padded = `${normalized}${'='.repeat((4 - normalized.length % 4) % 4)}`;
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function concatenate(...values: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const result = new Uint8Array(values.reduce((total, value) => total + value.byteLength, 0));
  let offset = 0;
  for (const value of values) {
    result.set(value, offset);
    offset += value.byteLength;
  }
  return result;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('DEVICE_DATABASE_OPEN_FAILED'));
  });
}

async function readStoredDevice(database: IDBDatabase): Promise<StoredBuyerDevice | null> {
  return new Promise((resolve, reject) => {
    const request = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(CURRENT_KEY);
    request.onsuccess = () => resolve((request.result as StoredBuyerDevice | undefined) ?? null);
    request.onerror = () => reject(request.error ?? new Error('DEVICE_READ_FAILED'));
  });
}

async function storeDevice(database: IDBDatabase, device: StoredBuyerDevice): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put(device, CURRENT_KEY);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('DEVICE_WRITE_FAILED'));
  });
}

async function createDevice(): Promise<StoredBuyerDevice> {
  const [encryptionPair, signingPair] = await Promise.all([
    crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']),
    crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, true, ['sign', 'verify']),
  ]);
  const [encryptionPublic, encryptionPrivateJwk, signingPublic, signingPrivateJwk] =
    await Promise.all([
      crypto.subtle.exportKey('jwk', encryptionPair.publicKey),
      crypto.subtle.exportKey('jwk', encryptionPair.privateKey),
      crypto.subtle.exportKey('jwk', signingPair.publicKey),
      crypto.subtle.exportKey('jwk', signingPair.privateKey),
    ]);
  const [encryptionPrivateKey, signingPrivateKey] = await Promise.all([
    crypto.subtle.importKey(
      'jwk', encryptionPrivateJwk, { name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits'],
    ),
    crypto.subtle.importKey(
      'jwk', signingPrivateJwk, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign'],
    ),
  ]);
  return {
    deviceId: `bdev_${base64Url(crypto.getRandomValues(new Uint8Array(24)))}`,
    encryptionPublicKey: JSON.stringify(encryptionPublic),
    signingPublicKey: JSON.stringify(signingPublic),
    encryptionPrivateKey,
    signingPrivateKey,
  };
}

export async function getOrCreateBuyerDevice(): Promise<StorefrontBuyerDeviceRegistration> {
  const database = await openDatabase();
  try {
    const stored = await readStoredDevice(database);
    if (stored) {
      return {
        deviceId: stored.deviceId,
        encryptionPublicKey: stored.encryptionPublicKey,
        signingPublicKey: stored.signingPublicKey,
      };
    }
    const created = await createDevice();
    await storeDevice(database, created);
    return {
      deviceId: created.deviceId,
      encryptionPublicKey: created.encryptionPublicKey,
      signingPublicKey: created.signingPublicKey,
    };
  } finally {
    database.close();
  }
}

export async function unwrapBuyerCatalogKey(input: {
  buyerId: string;
  deviceId: string;
  envelope: StorefrontCatalogKeyEnvelope;
  storefrontId: string;
}): Promise<CryptoKey> {
  const database = await openDatabase();
  try {
    const stored = await readStoredDevice(database);
    if (!stored || stored.deviceId !== input.deviceId) throw new Error('CATALOG_DEVICE_KEY_MISSING');
    const publicJwk = JSON.parse(input.envelope.ephemeralPublicKey) as JsonWebKey;
    const ephemeralPublicKey = await crypto.subtle.importKey(
      'jwk',
      publicJwk,
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      [],
    );
    const sharedSecret = new Uint8Array(await crypto.subtle.deriveBits(
      { name: 'ECDH', public: ephemeralPublicKey },
      stored.encryptionPrivateKey,
      256,
    ));
    const prefix = new TextEncoder().encode(
      `stoksypro:buyer-catalog-wrap:v1:${input.storefrontId}:${input.buyerId}:${input.deviceId}:`,
    );
    const wrappingKeyBytes = await crypto.subtle.digest('SHA-256', concatenate(prefix, sharedSecret));
    const wrappingKey = await crypto.subtle.importKey(
      'raw', wrappingKeyBytes, { name: 'AES-GCM' }, false, ['decrypt'],
    );
    const aad = new TextEncoder().encode(
      `stoksypro:buyer-catalog-envelope:v1:${input.storefrontId}:${input.buyerId}:${input.deviceId}:${input.envelope.keyVersion}`,
    );
    const rawCatalogKey = await crypto.subtle.decrypt({
      name: 'AES-GCM',
      iv: fromBase64(input.envelope.nonce),
      additionalData: aad,
      tagLength: 128,
    }, wrappingKey, fromBase64(input.envelope.ciphertext));
    return crypto.subtle.importKey(
      'raw', rawCatalogKey, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'],
    );
  } finally {
    database.close();
  }
}
